import { useId, useState, type ReactNode } from 'react';
import { ArmchairIcon, BedIcon, BusIcon, SparkleIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { OutOfPolicyGlyph } from '../../policy/out_of_policy_mark.js';
import { OfferBadge } from '../../shared/results_page/offer_badge.js';
import type { BusCompanyView, BusOfferView, BusStopView } from '../models.js';
import './bus_offer_card.css';

export type BusOfferCardCopy = {
  /** O botão que abre a escolha de poltrona ("Selecionar"). */
  selectLabel: string;
  /** "/pessoa" — a unidade ao lado do preço. */
  perPersonLabel: string;
  /** O link que abre os detalhes da viagem ("Detalhes"). */
  detailsLabel: string;
};

export type BusOfferCardProps = {
  offer: BusOfferView;
  copy: BusOfferCardCopy;
  /** A viagem escolhida nesta perna — o card fica marcado (`aria-current`). */
  selected?: boolean;
  /** Uma escrita em voo: o botão trava. */
  busy?: boolean;
  /** O que abre INLINE sob o card — a escolha de poltrona por trecho (#2440).
   *  Presente, o `Selecionar` sai da coluna: quem fecha a escolha é o rodapé
   *  da própria expansão. */
  expansion?: ReactNode;
  /** O `Detalhes` abriu — o host pode buscar o que a busca não trouxe (os
   *  horários da conexão) e devolver em `offer.details`. Só na abertura. */
  onDetailsOpen?: (offerId: string) => void;
  onSelect(offerId: string): void;
};

/** O logo da viação com queda macia: logo quebrado vira o nome em texto. Guarda
 *  a URL que falhou, não um booleano (a lista reusa a posição entre buscas). */
function CompanyMark({ company }: { readonly company: BusCompanyView }): ReactNode {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  return (
    <span className="hui-bus-offer-company">
      {company.logoUrl && failedUrl !== company.logoUrl && (
        <img
          className="hui-bus-offer-logo"
          src={company.logoUrl}
          alt=""
          loading="lazy"
          onError={() => {
            setFailedUrl(company.logoUrl ?? null);
          }}
        />
      )}
      <span className="hui-bus-offer-company-name">{company.name}</span>
    </span>
  );
}

/** Uma ponta da viagem: hora e cidade em destaque, a rodoviária embaixo. */
function TripStop({
  stop,
  align,
}: {
  readonly stop: BusStopView;
  readonly align: 'start' | 'end';
}): ReactNode {
  return (
    <div className="hui-bus-offer-stop" data-align={align}>
      <strong className="hui-bus-offer-stop-place">
        {stop.timeLabel} {stop.cityLabel}
      </strong>
      <span className="hui-bus-offer-stop-station">{stop.stationLabel}</span>
    </div>
  );
}

/** O trajeto entre as pontas, na gramática da perna do voo (#6194): a duração
 *  em cima, o fio no meio e a condição embaixo. O fio é decorativo — um ponto
 *  em cada ponta e um ponto aberto por troca de ônibus; a duração e a condição
 *  são texto lido. */
function RouteLine({ offer }: { readonly offer: BusOfferView }): ReactNode {
  return (
    <div className="hui-bus-offer-path">
      <span className="hui-bus-offer-duration">{offer.durationLabel}</span>
      <span aria-hidden="true" className="hui-bus-offer-line">
        <span className="hui-bus-offer-dot" />
        {Array.from({ length: offer.connections }, (_, index) => (
          <span key={index} className="hui-bus-offer-dot" data-kind="connection" />
        ))}
        <span className="hui-bus-offer-dot" />
      </span>
      <span className="hui-bus-offer-condition">{offer.connectionLabel}</span>
    </div>
  );
}

/** A cabeça: glifo do ônibus, "Ida · Ter, 26 ago" e a viação à direita. A
 *  duração e a conexão moram no trajeto, sobre e sob o fio. */
function OfferHead({ offer }: { readonly offer: BusOfferView }): ReactNode {
  return (
    <div className="hui-bus-offer-head">
      <span className="hui-bus-offer-headline">
        <BusIcon aria-hidden="true" />
        <strong>{offer.headline}</strong>
      </span>
      <CompanyMark company={offer.company} />
    </div>
  );
}

/** O chip da classe com o glifo do fato: poltrona ou cama. */
function SeatClassChip({ offer }: { readonly offer: BusOfferView }): ReactNode {
  const Icon = offer.seatClassGlyph === 'bed' ? BedIcon : ArmchairIcon;
  return (
    <span className="hui-bus-offer-class">
      <Icon aria-hidden="true" />
      {offer.seatClassLabel}
    </span>
  );
}

/** A coluna da decisão: preço por pessoa (o escudo de fora-da-política colado
 *  nele), a classe e o `Selecionar`. */
function OfferSide({
  offer,
  copy,
  title,
  busy,
  showSelect,
  onSelect,
}: {
  readonly offer: BusOfferView;
  readonly copy: BusOfferCardCopy;
  readonly title: string;
  readonly busy: boolean;
  readonly showSelect: boolean;
  readonly onSelect: (offerId: string) => void;
}): ReactNode {
  return (
    <div className="hui-bus-offer-side">
      <p className="hui-bus-offer-price">
        <strong>
          {offer.outOfPolicyLabel && <OutOfPolicyGlyph label={offer.outOfPolicyLabel} />}
          {offer.priceLabel}
        </strong>
        <small>{copy.perPersonLabel}</small>
      </p>
      <SeatClassChip offer={offer} />
      {showSelect && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          // No palco estreito o botão desce para a linha inteira e é o alvo do
          // polegar: o degrau de toque do DS (44px), não os 28 do `sm`.
          className="hui-bus-offer-select w-full max-sm:h-11"
          aria-label={`${copy.selectLabel} — ${title}`}
          disabled={busy}
          onClick={() => {
            onSelect(offer.id);
          }}
        >
          {copy.selectLabel}
        </Button>
      )}
    </div>
  );
}

/**
 * O card de uma viagem de ônibus na disponibilidade determinística (#2440): a
 * cabeça (perna, dia e viação), as duas pontas com as rodoviárias e o trajeto
 * entre elas (duração e conexão), o `Detalhes` e, à direita, preço por pessoa, classe e
 * `Selecionar`. É um `<article>` com UM gesto principal, como o card do carro.
 */
export function BusOfferCard({
  offer,
  copy,
  selected = false,
  busy = false,
  expansion,
  onDetailsOpen,
  onSelect,
}: Readonly<BusOfferCardProps>): ReactNode {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const detailsId = useId();
  const title = `${offer.headline} · ${offer.departure.cityLabel} → ${offer.arrival.cityLabel} · ${offer.company.name}`;
  return (
    <article className="hui-bus-offer-card" aria-label={title} aria-current={selected || undefined}>
      {offer.badges.length > 0 && (
        <div className="hui-bus-offer-badges">
          {offer.badges.map((badge) => (
            <OfferBadge key={badge.id} badge={badge} />
          ))}
        </div>
      )}
      <div className="hui-bus-offer-main">
        <div className="hui-bus-offer-trip">
          <OfferHead offer={offer} />
          <div className="hui-bus-offer-route">
            <TripStop stop={offer.departure} align="start" />
            <RouteLine offer={offer} />
            <TripStop stop={offer.arrival} align="end" />
          </div>
          <div className="hui-bus-offer-foot">
            {offer.meritNote && (
              <p className="hui-bus-offer-merit">
                <SparkleIcon aria-hidden="true" weight="fill" />
                {offer.meritNote}
              </p>
            )}
            {offer.details.length > 0 && (
              <button
                type="button"
                className="hui-bus-offer-details-toggle"
                aria-expanded={detailsOpen}
                aria-controls={detailsId}
                onClick={() => {
                  if (!detailsOpen) onDetailsOpen?.(offer.id);
                  setDetailsOpen(!detailsOpen);
                }}
              >
                {copy.detailsLabel}
              </button>
            )}
          </div>
          {detailsOpen && (
            <ul id={detailsId} className="hui-bus-offer-details">
              {offer.details.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </div>
        <OfferSide
          offer={offer}
          copy={copy}
          title={title}
          busy={busy}
          showSelect={offer.selectable && !expansion}
          onSelect={onSelect}
        />
      </div>
      {expansion && <div className="hui-bus-offer-expansion">{expansion}</div>}
    </article>
  );
}
